import { Empty, Modal, Tabs, TabsProps, Typography } from 'antd';
import api from 'api';
import axios, { AxiosError, CancelToken } from 'axios';
import RowLoader from 'components/Sceletons/RowLoader';
import UploadingComponent from 'components/UploadingComponent/UploadingComponent';
import { TFunction } from 'i18next';
import { IDataRoom, IDocument, IFolder, IUsers } from 'interfaces';
import React from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setRooms } from 'store/slices/userDataSlice';
import DeleteRoomTreeSelect from './DeleteRoomTreeSelect';
import classes from './RoomAction.module.scss';
import { deleteStatusType } from './types';
import DeleteRoomUsersTable from './DeleteRoomUsersTable';
import { useNavigate, useParams } from 'react-router';
import cn from 'classnames';
import { clearReduxFunction } from 'store/reducers/userDataCreator';
import { getTheme } from 'helpers/getTheme';

type Props = {
  room: IDataRoom;
  t: TFunction;
  isOpen: boolean;
  onCancel: () => void;
};

const knownErrors = ["This room doesn't exists", 'This room is on agreement mode', 'This room has related documents'];

export default function DeleteRoomModal({ room, t, isOpen, onCancel }: Props) {
  const [isDeletingRoom, setIsDeletingRoom] = React.useState(false);
  const [documents, setDocuments] = React.useState<IDocument[]>([]);
  const [folders, setFolders] = React.useState<IFolder[]>([]);
  const [users, setUsers] = React.useState<IUsers[]>([]);
  const [deleteStatus, setDeleteStatus] = React.useState<deleteStatusType>('pending');
  const [isLoadingDocuments, setIsLoadingDocuments] = React.useState(false);

  const { available_rooms } = useAppSelector((state) => state.userData);
  const { messageApi } = useAppSelector((state) => state.windowState);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();

  React.useEffect(() => {
    const source = axios.CancelToken.source();
    fetchRoomDocuments(source.token);

    return () => source.cancel();
  }, []);

  const clearData = (clearCase: 'documents' | 'users') => {
    if (clearCase === 'documents') {
      setDocuments([]);
      setFolders([]);

      users.length === 0 && setDeleteStatus('deleteRoom');
    } else if (clearCase === 'users') {
      setUsers([]);

      documents.length === 0 && folders.length === 0 && setDeleteStatus('deleteRoom');
    }
  };

  const fetchRoomDocuments = async (sourceToken: CancelToken) => {
    try {
      setIsLoadingDocuments(true);
      const [response, foldersResponse, usersResponse] = await Promise.all([
        api.getDocuments({ room_id: room.id, action: '' }, sourceToken),
        api.getFolders(room.id, sourceToken),
        api.getUsers(room.id),
      ]);

      const withoutOwnerUsers = usersResponse.data.filter(({ id }) => id !== room.owner_id);
      setUsers(withoutOwnerUsers);
      setDocuments(response.data);
      setFolders(foldersResponse.data.map((folder) => ({ ...folder, type: 'folder' })));
      const isForDeleteElements = Boolean(
        withoutOwnerUsers.length || response.data.length || foldersResponse.data.length
      );
      setDeleteStatus(isForDeleteElements ? 'deleteElements' : 'deleteRoom');
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        err?.message !== 'canceled' &&  messageApi?.error(`${t('Rooms.modals.delete.failedGettingInfo')} - ${room?.name}`);
      }
    } finally {
      setIsLoadingDocuments(false);
    }
  };

  const onDeleteClick = async () => {
    setIsDeletingRoom(true);
    try {
      await api.deleteRoom({ room_id: room?.id });
      const filteredRooms = available_rooms.filter((available_room) => available_room.id !== room.id);
      document.getElementById(`room_card_${room.id}`)?.classList.toggle(classes.slickHide);
      
      if (params.room === room?.id) {
        dispatch(clearReduxFunction({}));
        params.room === room?.id && navigate('/rooms');
      };

      setTimeout(() => {
        dispatch(setRooms(filteredRooms));
      }, 400);

      messageApi?.success(`${t('Rooms.modals.delete.room')} '${room?.name}' ${t('Rooms.modals.delete.wasDeleted')}`);
    } catch (e: AxiosError | unknown ) {
      if (axios.isAxiosError(e)) {
        const errorText = e.response?.data?.detail?.message;
        const isKnownError = knownErrors.includes(errorText);

        isKnownError
          ? messageApi?.warning(
              `${t('Rooms.modals.delete.failedToDelete')} "${room?.name}". ${t(`Rooms.modals.delete.${errorText}`)}`,
              6
            )
          : messageApi?.error(
              `${t('Rooms.modals.delete.failedToDelete')} "${room?.name}": "${e?.message || 'unknown error'}"`,
              6
            );
      }
    } finally {
      onCancel();
      setIsDeletingRoom(false);
    }
  };

  const items: TabsProps['items'] = [
    {
      key: 'documents',
      children:
        !documents.length && !folders.length ? (
          <Empty rootClassName={classes.margin} description={t('Documents.table.fallback')} />
        ) : (
          <DeleteRoomTreeSelect t={t} clearData={clearData} room={room} folders={folders} documents={documents} />
        ),
      label: t('sidebar.documents'),
    },
    {
      key: 'users',
      children: !users.length ? (
        <Empty rootClassName={classes.margin} description={t('Users.table.empty')} />
      ) : (
        <DeleteRoomUsersTable room={room} users={users} clearData={clearData} t={t} />
      ),
      label: t('sidebar.users'),
    },
  ];

  const deleteCases = {
    pending: [1, 2, 3].map((el) => <RowLoader key={`loader-${el}`} width={480} height={60} />),
    deleteElements: (
      <div>
        <div style={{fontSize: 15, fontWeight: 500 }}>{t('Rooms.modals.delete.deleteElementsTitle')}</div>
        <Tabs items={items} />
      </div>
    ),
    deleteRoom: (
      <div style={{paddingBottom: 15}}>
        {t('Rooms.modals.delete.body')} <b>"{room?.name}"?</b>
      </div>
    ),
  };

  return (
    <Modal
      title={`${t('Rooms.modals.delete.title')} ${room?.name}`}
      onOk={onDeleteClick}
      className={cn(
        classes.modalWrap,
        deleteStatus === 'deleteRoom' ? 'null' : classes.withoutFooter,
        getTheme()
      )}
      okButtonProps={{
        disabled: isLoadingDocuments || Boolean(documents.length) || Boolean(folders.length) || Boolean(users.length),
      }}
      cancelButtonProps={{ style: {display: 'none'} }}
      okText={t('Documents.contextMenu.delete')}
      open={isOpen}
      onCancel={onCancel}
      confirmLoading={isDeletingRoom}
      centered
      footer={(simpleFooter) => {
        return deleteStatus === 'deleteRoom' ? simpleFooter : <div />
      }}
    >
      <div>{deleteCases[deleteStatus]}</div>
      {isDeletingRoom && <UploadingComponent />}
    </Modal>
  );
}
